import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";

import Login from "./components/Login/Login";
import Dashboard from "./components/Dashboard/Dashboard";
import LeadForm from "./components/LeadForm/LeadForm";
import OrderForm from "./components/OrderForm/OrderForm";
import EngineerForm from "./components/EngineerForm/EngineerForm";
import NightForm from "./components/NightForm/NightForm";
import EventDetails from "./components/EventDetails/EventDetails";
import RequestAssignmentForm from "./components/RequestAssignmentForm/RequestAssignmentForm";
import CoordinatesForm from "./components/CoordinatesForm/CoordinatesForm";
import PhotoForm from "./components/PhotoForm/PhotoForm";

import styles from "./App.module.css";
import toggleStyles from "./components/_shared/ThemeToggle.module.css";

const PUSH_CONTROLS_ENABLED = true;

function getApiBaseUrl() {
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:5000";
  }

  return window.location.origin;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);

  const base64 = `${base64String}${padding}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);

  return Uint8Array.from(
    [...rawData].map((character) => character.charCodeAt(0))
  );
}

function getPushUserId(currentUser) {
  const userId =
    currentUser?.id ||
    currentUser?.userId ||
    currentUser?.user_id ||
    currentUser?.employeeId ||
    currentUser?.uid ||
    currentUser?.guid ||
    currentUser?.uuid ||
    "";

  return userId ? String(userId) : "";
}

function PrivateRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  return currentUser ? children : <Navigate to="/" replace />;
}

function RightsProtectedRoute({ children, requiredRight }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;

  if (!currentUser) return <Navigate to="/" replace />;

  // Берём права либо из currentUser (если ты туда положил rights), либо из localStorage
  let rights = currentUser?.rights;
  if (!rights) {
    try {
      rights = JSON.parse(localStorage.getItem("userRights") || "{}");
    } catch {
      rights = {};
    }
  }

  if (!rights?.[requiredRight]) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppContent() {
  // themeMode: "auto" | "light" | "dark"
  const getInitialThemeMode = () => {
    const saved = localStorage.getItem("themeMode");
    if (saved === "light" || saved === "dark" || saved === "auto") return saved;
    return "auto";
  };

  const [themeMode, setThemeMode] = useState(getInitialThemeMode);

  const [prefersDark, setPrefersDark] = useState(() => {
    return (
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false
    );
  });

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;

    const onChange = (e) => setPrefersDark(e.matches);

    // Safari может не поддерживать addEventListener
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const effectiveTheme = useMemo(() => {
    if (themeMode === "auto") return prefersDark ? "dark" : "light";
    return themeMode;
  }, [themeMode, prefersDark]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("theme-light", "theme-dark");

    if (themeMode === "light") root.classList.add("theme-light");
    if (themeMode === "dark") root.classList.add("theme-dark");

    localStorage.setItem("themeMode", themeMode);
  }, [themeMode]);

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const userName = currentUser?.name || currentUser?.username || "";
  const rights = useMemo(() => {
  if (currentUser?.rights && typeof currentUser.rights === "object") {
    return currentUser.rights;
  }

  try {
    return JSON.parse(localStorage.getItem("userRights") || "{}");
  } catch {
    return {};
  }
}, [currentUser]);

const canUsePushControls = PUSH_CONTROLS_ENABLED;
  

  const [pushStatus, setPushStatus] = useState("checking");
const [pushBusy, setPushBusy] = useState(false);

const isDevelopment = process.env.NODE_ENV === "development";

    const pushButtonTitle =
  {
    checking: "Проверка уведомлений",
    disabled: "Включить уведомления",
    enabled: "Отключить уведомления",
    denied: "Уведомления запрещены в браузере",
    unsupported: "Push-уведомления не поддерживаются",
  }[pushStatus] || "Уведомления";

const savePushSubscription = useCallback(
  async (subscription, userId) => {
    const response = await fetch(
      `${getApiBaseUrl()}/api/push/subscribe`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          userId,
          subscription: subscription.toJSON(),
        }),
      }
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.message || `Ошибка сохранения подписки: ${response.status}`
      );
    }

    return data;
  },
  []
);

useEffect(() => {
    if (!canUsePushControls) {
  setPushStatus("disabled");
  return;
}
  let cancelled = false;

  const checkPushStatus = async () => {
    if (!currentUser) {
      setPushStatus("checking");
      return;
    }

    const isSupported =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;

    if (!isSupported) {
      setPushStatus("unsupported");
      return;
    }

    if (Notification.permission === "denied") {
      setPushStatus("denied");
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        await registration.pushManager.getSubscription();

      if (cancelled) return;

      if (!subscription) {
        setPushStatus("disabled");
        return;
      }

      setPushStatus("enabled");

      // Перепривязываем существующую подписку к текущему пользователю.
      // Это заменит временный user_id local-test.
      const userId = getPushUserId(currentUser);

      if (userId) {
        try {
          await savePushSubscription(subscription, userId);
        } catch (error) {
          console.error(
            "Ошибка синхронизации push-подписки:",
            error
          );
        }
      }
    } catch (error) {
      console.error("Ошибка проверки push-подписки:", error);

      if (!cancelled) {
        setPushStatus("disabled");
      }
    }
  };

  checkPushStatus();

  return () => {
    cancelled = true;
  };
}, [currentUser, savePushSubscription, canUsePushControls]);

const handleEnablePush = async () => {
  if (pushBusy) return;

  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    setPushStatus("unsupported");
    window.alert("Этот браузер не поддерживает push-уведомления");
    return;
  }

  if (Notification.permission === "denied") {
    setPushStatus("denied");

    window.alert(
      "Уведомления запрещены в настройках браузера. Разрешите их для этого сайта."
    );

    return;
  }

  const userId = getPushUserId(currentUser);

  if (!userId) {
    window.alert("Не удалось определить ID текущего пользователя");
    return;
  }

  try {
    setPushBusy(true);

    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      setPushStatus(
        permission === "denied" ? "denied" : "disabled"
      );

      return;
    }

    const keyResponse = await fetch(
      `${getApiBaseUrl()}/api/push/public-key`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const keyData = await keyResponse.json().catch(() => null);

    if (!keyResponse.ok || !keyData?.publicKey) {
      throw new Error(
        keyData?.message || "Не удалось получить публичный VAPID-ключ"
      );
    }

    const registration = await navigator.serviceWorker.ready;

    let subscription =
      await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          keyData.publicKey
        ),
      });
    }

    await savePushSubscription(subscription, userId);

    setPushStatus("enabled");
  } catch (error) {
    console.error("Ошибка включения уведомлений:", error);

    window.alert(
      error?.message || "Не удалось включить уведомления"
    );

    setPushStatus("disabled");
  } finally {
    setPushBusy(false);
  }
};
const handleDisablePush = async () => {
  if (pushBusy) return;

  try {
    setPushBusy(true);

    const registration = await navigator.serviceWorker.ready;
    const subscription =
      await registration.pushManager.getSubscription();

    if (!subscription) {
      setPushStatus("disabled");
      return;
    }

    const endpoint = subscription.endpoint;

    const unsubscribed = await subscription.unsubscribe();

    if (!unsubscribed) {
      throw new Error("Браузер не смог отключить push-подписку");
    }

    const response = await fetch(
      `${getApiBaseUrl()}/api/push/unsubscribe`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          endpoint,
        }),
      }
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      // На устройстве подписка уже отключена.
      // Устаревшая запись в БД позже удалится при ответе 404/410.
      console.error(
        "Не удалось удалить подписку из БД:",
        data?.message || response.status
      );
    }

    setPushStatus("disabled");
  } catch (error) {
    console.error("Ошибка отключения уведомлений:", error);

    window.alert(
      error?.message || "Не удалось отключить уведомления"
    );
  } finally {
    setPushBusy(false);
  }
};

const handleTogglePush = async () => {
  if (pushStatus === "enabled") {
    await handleDisablePush();
    return;
  }

  await handleEnablePush();
};

const handleTestPush = async () => {
  if (pushBusy) return;

  const userId = getPushUserId(currentUser);

  if (!userId) {
    window.alert("Не удалось определить ID текущего пользователя");
    return;
  }

  try {
    setPushBusy(true);

    const response = await fetch(
      `${getApiBaseUrl()}/api/push/test`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          userId,
        }),
      }
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.message || `Ошибка отправки: ${response.status}`
      );
    }

    if (!data?.sent) {
      throw new Error("Backend не отправил уведомление ни на одно устройство");
    }

    console.log("Результат тестового push:", data);
  } catch (error) {
    console.error("Ошибка тестового push:", error);

    window.alert(
      error?.message || "Не удалось отправить тестовое уведомление"
    );
  } finally {
    setPushBusy(false);
  }
};

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  const toggleTheme = () => {
    // Клик всегда фиксирует тему (выход из auto)
    setThemeMode(effectiveTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className={styles.app}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <a href="/dashboard">
            <div>
              <h1>MF-Group Dashboard</h1>
            </div>
          </a>

          {currentUser && (
            <div className={toggleStyles.wrapper}>
              <span className={styles.userNameHeader}>{userName}</span>
              {canUsePushControls && (
  <>
    <button
      type="button"
      className={`${styles.notificationButton} ${
        pushStatus === "enabled"
          ? styles.notificationButtonActive
          : ""
      }`}
      onClick={handleTogglePush}
      disabled={
        pushBusy ||
        pushStatus === "checking" ||
        pushStatus === "denied" ||
        pushStatus === "unsupported"
      }
      aria-label={pushButtonTitle}
      title={pushButtonTitle}
    >
      {pushBusy ? (
  <span className={styles.notificationBusy}>…</span>
) : (
  <img
    className={styles.notificationIcon}
    src={
      pushStatus === "enabled"
        ? "/notification.png"
        : "/notification-off.png"
    }
    alt=""
    aria-hidden="true"
  />
)}
    </button>

    {isDevelopment && pushStatus === "enabled" && (
      <button
        type="button"
        className={styles.pushTestButton}
        onClick={handleTestPush}
        disabled={pushBusy}
        title="Отправить тестовое уведомление"
      >
        Тест push
      </button>
    )}
  </>
)}

              <button
                type="button"
                className={toggleStyles.toggle}
                onClick={toggleTheme}
                aria-label="Переключить тему"
                title="Тема"
              >
                <img
                  className={toggleStyles.icon}
                  src={
                    effectiveTheme === "light"
                      ? "/light-on.png"
                      : "/light-off.png"
                  }
                  alt=""
                  aria-hidden="true"
                />
              </button>

              <button onClick={handleLogout} className={styles.logoutButton}>
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      <main className={styles.main}>
        <Routes>
          <Route path="/" element={<Login />} />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/lead-form"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="newLead">
                  <LeadForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/order-form"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="orderAuto">
                  <OrderForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/engineer-form"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="recordToEngineer">
                  <EngineerForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/night-form"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="eventFromNightWork">
                  <NightForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/events/:eventId"
            element={
              <PrivateRoute>
                <EventDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/request-assignment"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="dispatcher">
                  <RequestAssignmentForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/coordinates-form"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="newLead">
                  <CoordinatesForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/photo-form"
            element={
              <PrivateRoute>
                <RightsProtectedRoute requiredRight="photoUpload">
                  <PhotoForm />
                </RightsProtectedRoute>
              </PrivateRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
